import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { userTypeOf } from '../../util/userType';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { setInitialValues as setInitialValuesForPaymentMethods } from '../../ducks/paymentMethods.duck';
import { parse } from '../../util/urlHelpers';
import { createCheckoutSession, paymentHistory, paymentInvoiceLink } from '../../util/api';

// ================ Action types ================ //

export const SETUP_INTENT_REQUEST = 'app/PaymentMethodsPage/SETUP_INTENT_REQUEST';
export const SETUP_INTENT_SUCCESS = 'app/PaymentMethodsPage/SETUP_INTENT_SUCCESS';
export const SETUP_INTENT_ERROR = 'app/PaymentMethodsPage/SETUP_INTENT_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/PaymentMethodsPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/PaymentMethodsPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/PaymentMethodsPage/STRIPE_CUSTOMER_ERROR';

export const BUY_MESSAGE_PACK_REQUEST = 'app/PaymentMethodsPage/BUY_MESSAGE_PACK_REQUEST';
export const BUY_MESSAGE_PACK_SUCCESS = 'app/PaymentMethodsPage/BUY_MESSAGE_PACK_SUCCESS';
export const BUY_MESSAGE_PACK_ERROR = 'app/PaymentMethodsPage/BUY_MESSAGE_PACK_ERROR';


export const BUY_JOB_SLOTS_REQUEST = 'app/PaymentMethodsPage/BUY_JOB_SLOTS_REQUEST';
export const BUY_JOB_SLOTS_SUCCESS = 'app/PaymentMethodsPage/BUY_JOB_SLOTS_SUCCESS';
export const BUY_JOB_SLOTS_ERROR = 'app/PaymentMethodsPage/BUY_JOB_SLOTS_ERROR';

export const BUY_RFP_SLOTS_REQUEST = 'app/PaymentMethodsPage/BUY_RFP_SLOTS_REQUEST';
export const BUY_RFP_SLOTS_SUCCESS = 'app/PaymentMethodsPage/BUY_RFP_SLOTS_SUCCESS';
export const BUY_RFP_SLOTS_ERROR = 'app/PaymentMethodsPage/BUY_RFP_SLOTS_ERROR';

export const FETCH_PAYMENT_HISTORY_REQUEST = 'app/PaymentsHistoryPage/FETCH_PAYMENT_HISTORY_REQUEST';
export const FETCH_PAYMENT_HISTORY_SUCCESS = 'app/PaymentsHistoryPage/FETCH_PAYMENT_HISTORY_SUCCESS';
export const FETCH_PAYMENT_HISTORY_ERROR = 'app/PaymentsHistoryPage/FETCH_PAYMENT_HISTORY_ERROR';


// ================ Reducer ================ //

const initialState = {
  setupIntentInProgress: false,
  setupIntentError: null,
  setupIntent: null,
  stripeCustomerFetched: false,
  stripeCustomerFetchError: null,
  buyMessagePackInProgress: false,
  buyMessagePackError: null,
  buyMessagePackSuccess: null,
  buyJobSlotsInProgress: false,
  buyJobSlotsError: null,
  buyJobSlotsSuccess: null,
  buyRFPSlotsInProgress: false,
  buyRFPSlotsError: null,
  buyRFPSlotsSuccess: null,
  paymentHistory: { invoices: [], pagination: null },
  paymentHistoryInProgress: false,
  paymentHistoryError: null,
  // The latest page request made. An answer to any earlier one is ignored, so a slow answer to a
  // page the member has already left never replaces the page they moved to.
  paymentHistoryRequestId: 0,
};

export default function payoutMethodsPageReducer(state = initialState, action = {}) {
  const { type, payload, meta } = action;
  switch (type) {
    case SETUP_INTENT_REQUEST:
      return { ...state, setupIntentInProgress: true, setupIntentError: null };
    case SETUP_INTENT_SUCCESS:
      return {
        ...state,
        setupIntentInProgress: false,
        setupIntentError: null,
        setupIntent: payload,
      };
    case SETUP_INTENT_ERROR:
      return { ...state, setupIntentInProgress: false, setupIntentError: null };
    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      return { ...state, stripeCustomerFetchError: payload };
    
    case BUY_MESSAGE_PACK_REQUEST:
      return { ...state, buyMessagePackInProgress: true, buyMessagePackError: null };
    case BUY_MESSAGE_PACK_SUCCESS:
      const { url } = payload;
      window.location.href = url;

      return {
        ...state,
        buyMessagePackInProgress: false,
        buyMessagePackError: null,
        buyMessagePackSuccess: payload,
      };
    case BUY_MESSAGE_PACK_ERROR:
      return { ...state, buyMessagePackInProgress: false, buyMessagePackError: payload };

    case BUY_JOB_SLOTS_REQUEST:
      return { ...state, buyJobSlotsInProgress: true, buyJobSlotsError: null };
    case BUY_JOB_SLOTS_SUCCESS:
      const { url: jobSlotsUrl } = payload;
      window.location.href = jobSlotsUrl;

      return {
        ...state,
        buyJobSlotsInProgress: false,
        buyJobSlotsError: null,
        buyJobSlotsSuccess: payload,
      };
    case BUY_JOB_SLOTS_ERROR:
      return { ...state, buyJobSlotsInProgress: false, buyJobSlotsError: payload };

    case BUY_RFP_SLOTS_REQUEST:
      return { ...state, buyRFPSlotsInProgress: true, buyRFPSlotsError: null };
    case BUY_RFP_SLOTS_SUCCESS:
      const { url: rfpSlotsUrl } = payload;
      window.location.href = rfpSlotsUrl;

      return {
        ...state,
        buyRFPSlotsInProgress: false,
        buyRFPSlotsError: null,
        buyRFPSlotsSuccess: payload,
      };
    case BUY_RFP_SLOTS_ERROR:
      return { ...state, buyRFPSlotsInProgress: false, buyRFPSlotsError: payload };

    case FETCH_PAYMENT_HISTORY_REQUEST:
      return {
        ...state,
        paymentHistoryInProgress: true,
        paymentHistoryError: null,
        paymentHistoryRequestId: meta?.requestId,
      };
    case FETCH_PAYMENT_HISTORY_SUCCESS:
      if (meta?.requestId !== state.paymentHistoryRequestId) {
        return state;
      }
      return {
        ...state,
        paymentHistoryInProgress: false,
        paymentHistory: {
          invoices: payload?.invoices || [],
          pagination: payload?.pagination || null,
        },
      };
    case FETCH_PAYMENT_HISTORY_ERROR:
      if (meta?.requestId !== state.paymentHistoryRequestId) {
        return state;
      }
      return { ...state, paymentHistoryInProgress: false, paymentHistoryError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setupIntentRequest = () => ({ type: SETUP_INTENT_REQUEST });
export const setupIntentSuccess = () => ({ type: SETUP_INTENT_SUCCESS });
export const setupIntentError = e => ({
  type: SETUP_INTENT_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const buyMessagePackRequest = () => ({ type: BUY_MESSAGE_PACK_REQUEST });
export const buyMessagePackSuccess = (response) => ({ 
  type: BUY_MESSAGE_PACK_SUCCESS,
  payload: response,
});
export const buyMessagePackError = e => ({
  type: BUY_MESSAGE_PACK_ERROR,
  error: true,
  payload: e,
});

export const buyJobSlotsRequest = () => ({ type: BUY_JOB_SLOTS_REQUEST });
export const buyJobSlotsSuccess = (response) => ({
  type: BUY_JOB_SLOTS_SUCCESS,
  payload: response,
});
export const buyJobSlotsError = e => ({
  type: BUY_JOB_SLOTS_ERROR,
  error: true,
  payload: e,
});

export const buyRFPSlotsRequest = () => ({ type: BUY_RFP_SLOTS_REQUEST });
export const buyRFPSlotsSuccess = (response) => ({
  type: BUY_RFP_SLOTS_SUCCESS,
  payload: response,
});
export const buyRFPSlotsError = e => ({
  type: BUY_RFP_SLOTS_ERROR,
  error: true,
  payload: e,
});

export const fetchPaymentHistoryRequest = requestId => ({
  type: FETCH_PAYMENT_HISTORY_REQUEST,
  meta: { requestId },
});
export const fetchPaymentHistorySuccess = (response, requestId) => ({
  type: FETCH_PAYMENT_HISTORY_SUCCESS,
  payload: response,
  meta: { requestId },
});
export const fetchPaymentHistoryError = (e, requestId) => ({
  type: FETCH_PAYMENT_HISTORY_ERROR,
  error: true,
  payload: e,
  meta: { requestId },
});

// ================ Thunks ================ //

export const createStripeSetupIntent = () => (dispatch, getState, sdk) => {
  dispatch(setupIntentRequest());
  return sdk.stripeSetupIntents
    .create()
    .then(response => {
      const setupIntent = response.data.data;
      dispatch(setupIntentSuccess(setupIntent));
      return setupIntent;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'create-setup-intent-failed');
      dispatch(setupIntentError(error));
      return { createStripeSetupIntentSuccess: false };
    });
};

export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'fetch-stripe-customer-failed');
      dispatch(stripeCustomerError(error));
    });
};

// The categories the page offers and the server accepts.
export const PAYMENT_CATEGORIES = {
  WALLET: 'wallet',
  RFP_APPLICATIONS: 'rfp_applications',
  MESSAGE_PACKS: 'message_packs',
  JOB_RFP_SLOTS: 'job_rfp_slots',
  SUBSCRIPTIONS: 'subscriptions',
  INTEGRITY_CREDITS: 'integrity_credits',
  SKILLFIT_CREDITS: 'skillfit_credits',
};

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAX_SEARCH_LENGTH = 100;

// A YYYY-MM-DD day as a Date in the browser's own timezone, or null when the text is not a real
// calendar day (e.g. 2026-02-31 would otherwise roll over into March).
const localDay = (text, hours, minutes, seconds, ms) => {
  const match = typeof text === 'string' ? text.match(DAY_PATTERN) : null;
  if (!match) {
    return null;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
  const date = new Date(year, month, day, hours, minutes, seconds, ms);
  const isSameDay =
    date.getFullYear() === year && date.getMonth() === month && date.getDate() === day;
  return isSameDay ? date : null;
};

/**
 * Read the page's filters from the URL query: `page`, `category`, `from` and `to` (YYYY-MM-DD)
 * and `q`. Anything malformed is dropped, so a hand-edited URL falls back to the unfiltered list;
 * that includes a category the page does not offer.
 *
 * @param {String} search - the location's query string
 * @returns {Object} { page, category, from, to, q }, each undefined when not set
 */
export const parsePaymentHistoryFilters = search => {
  const query = parse(search || '');
  const page = Number.isInteger(query.page) && query.page > 0 ? query.page : undefined;
  const category = Object.values(PAYMENT_CATEGORIES).includes(query.category)
    ? query.category
    : undefined;
  const from = localDay(query.from, 0, 0, 0, 0) ? query.from : undefined;
  const to = localDay(query.to, 0, 0, 0, 0) ? query.to : undefined;
  const q =
    query.q != null && String(query.q).trim()
      ? String(query.q)
          .trim()
          .slice(0, MAX_SEARCH_LENGTH)
      : undefined;
  return { page, category, from, to, q };
};

/**
 * The payment history request for a URL query. The date range covers whole days in the
 * member's own timezone: `from` starts at local midnight and `to` ends at the last second of
 * that local day, both sent as epoch seconds.
 *
 * @param {String} search - the location's query string
 * @returns {Object} request body for the payment history endpoint
 */
export const paymentHistoryQueryFromSearch = search => {
  const { page, category, from, to, q } = parsePaymentHistoryFilters(search);
  const toSeconds = date => Math.floor(date.getTime() / 1000);
  return {
    ...(page ? { page } : {}),
    ...(category ? { category } : {}),
    ...(from ? { createdFrom: toSeconds(localDay(from, 0, 0, 0, 0)) } : {}),
    ...(to ? { createdTo: toSeconds(localDay(to, 23, 59, 59, 999)) } : {}),
    ...(q ? { search: q } : {}),
  };
};

export const fetchPaymentHistory = query => (dispatch, getState, sdk) => {
  // Numbered from the store rather than a module counter, so a server render never shares one
  // between visitors.
  const requestId = (getState().PaymentsHistoryPage?.paymentHistoryRequestId || 0) + 1;
  dispatch(fetchPaymentHistoryRequest(requestId));

  return paymentHistory(query)
    .then(response => {
      dispatch(fetchPaymentHistorySuccess(response, requestId));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'fetch-payment-history-failed');
      dispatch(fetchPaymentHistoryError(error, requestId));
    });
};

// Resolves to a fresh link for an invoice's PDF (`kind: 'pdf'`) or hosted page (`kind: 'hosted'`).
// Nothing is kept in the store: the links expire shortly after they are issued.
export const fetchPaymentInvoiceLink = ({ invoiceId, kind }) => (dispatch, getState, sdk) => {
  return paymentInvoiceLink({ invoiceId, kind }).then(response => response?.url);
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  dispatch(setInitialValuesForPaymentMethods());

  return Promise.all([
    dispatch(fetchPaymentHistory(paymentHistoryQueryFromSearch(search))),
    dispatch(stripeCustomer()),
  ]);
};

export const buyMessagesPack = (priceId, quantity=1, type='messagePack') => async (dispatch, getState, sdk) => {
  dispatch(buyMessagePackRequest());
  
  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileListing = state.user?.profileListing;
  
  const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId ||
                    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let email = null;
  let adminId = '';

  if (!profileListingId) {
    return;
  }

  if (userTypeOf(currentUser) === 'candidate') {
    adminId = currentUser?.id?.uuid;
    email = currentUser?.attributes?.email;
  } else {
    // let profileListing = await sdk.listings.show({ id: profileListingId });
    // profileListing = profileListing?.data?.data;
    
    email = currentUser?.attributes?.email;
    
    adminId = profileListing?.attributes?.publicData?.adminId;
    // if (adminId) {
    //   const adminUserResponse = await getUserDetails({ userId: adminId });
    //   const adminUser = adminUserResponse?.data?.data;
    //   email = adminUser?.attributes?.email;
    // }
  }

  if (!email) {
    return;
  }

  return createCheckoutSession({ priceId, quantity, type, email, paidBy: adminId, isMessagePack: true })
    .then(response => {
      dispatch(buyMessagePackSuccess(response));
      return response;
    })
    .catch(error => {
      dispatch(buyMessagePackError(error));
    })

};


export const buyJobRFPSlots = (priceId, quantity, type, isJobSlot = true) => async (dispatch, getState, sdk) => {
  if (isJobSlot) {
    dispatch(buyJobSlotsRequest());
  } else {
    dispatch(buyRFPSlotsRequest());
  }

  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileListing = state.user?.profileListing;

  const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId ||
                    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let email = null;
  let adminId = '';

  if (!profileListingId) {
    return;
  }

  if (userTypeOf(currentUser) === 'candidate') {
    adminId = currentUser?.id?.uuid;
    email = currentUser?.attributes?.email;
  } else {
    adminId = profileListing?.attributes?.publicData?.adminId;
    email = currentUser?.attributes?.email;
  }

  if (!email) {
    return;
  }

  return createCheckoutSession({
    priceId,
    quantity,
    type: `${type}Slot`,
    email,
    paidBy: adminId,
    isJobRFPSlot: true,
    slotType: type
  })
    .then(response => {
      if (isJobSlot) {
        dispatch(buyJobSlotsSuccess(response));
      } else {
        dispatch(buyRFPSlotsSuccess(response));
      }
      return response;
    })
    .catch(error => {
      if (isJobSlot) {
        dispatch(buyJobSlotsError(error));
      } else {
        dispatch(buyRFPSlotsError(error));
      }
    });
};
